import type { UserProgress } from "./gym/types";
import type { FavoriteItem } from "../hooks/useStudentFavorites";
import type { CompletedTask, PracticeRecord, StudentStats } from "../hooks/useStudentStats";
import { trainingPlan } from "./gym/trainingPlan";

export const GYM_PROGRESS_KEY = "bordona-gym-progress";
export const FAVORITES_KEY = "bordona-student-favorites";
export const STATS_KEY = "bordona-student-stats";
export const TASKS_KEY = "bordona-completed-tasks";
export const PRACTICE_KEY = "bordona-practice-sessions";
export const ACTIVE_DAYS_KEY = "bordona-active-days";
export const SONG_STUDIO_SESSIONS_KEY = "bordona-song-studio-sessions";

export interface SongStudioSessionRecord {
  id: string;
  songId: string;
  songTitle: string;
  artist?: string;
  mode: "learn" | "practice";
  accuracy: number;
  hit: number;
  late: number;
  total: number;
  completedAt: number;
}

function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function loadGymProgress(): UserProgress {
  const p = loadJSON<Partial<UserProgress> | null>(GYM_PROGRESS_KEY, null);
  if (!p) {
    return {
      completedExercises: [],
      currentExercise: null,
      scores: {},
      totalPracticeTime: 0,
      streakDays: 0,
      lastPracticeDate: null,
    };
  }
  return {
    completedExercises: Array.isArray(p.completedExercises) ? p.completedExercises : [],
    currentExercise: p.currentExercise ?? null,
    scores: p.scores && typeof p.scores === "object" ? p.scores : {},
    totalPracticeTime: typeof p.totalPracticeTime === "number" ? p.totalPracticeTime : 0,
    streakDays: typeof p.streakDays === "number" ? p.streakDays : 0,
    lastPracticeDate: p.lastPracticeDate ?? null,
  };
}

export function loadFavorites(): FavoriteItem[] {
  return loadJSON<FavoriteItem[]>(FAVORITES_KEY, []);
}

export function loadStudentStats(): StudentStats {
  return loadJSON<StudentStats>(STATS_KEY, {
    tasksCompleted: 0,
    practiceSessions: 0,
    totalPracticeMinutes: 0,
    averageScore: 0,
    currentStreak: 0,
    longestStreak: 0,
    explorations: 0,
    lastActiveDate: null,
  });
}

export function loadCompletedTasks(): CompletedTask[] {
  return loadJSON<CompletedTask[]>(TASKS_KEY, []);
}

export function loadPracticeRecords(): PracticeRecord[] {
  return loadJSON<PracticeRecord[]>(PRACTICE_KEY, []);
}

export function loadActiveDays(): string[] {
  const map = loadJSON<Record<string, boolean>>(ACTIVE_DAYS_KEY, {});
  return Object.keys(map).sort();
}

export function loadSongStudioSessions(): SongStudioSessionRecord[] {
  return loadJSON<SongStudioSessionRecord[]>(SONG_STUDIO_SESSIONS_KEY, []);
}

export function appendSongStudioSession(
  record: Omit<SongStudioSessionRecord, "id" | "completedAt">
): void {
  const prev = loadSongStudioSessions();
  const entry: SongStudioSessionRecord = {
    ...record,
    id: crypto.randomUUID(),
    completedAt: Date.now(),
  };
  localStorage.setItem(
    SONG_STUDIO_SESSIONS_KEY,
    JSON.stringify([entry, ...prev].slice(0, 80))
  );
}

export function getGymLevelSummaries(progress: UserProgress) {
  return trainingPlan.map((level) => {
    const total = level.exercises.length;
    const done = level.exercises.filter((ex) =>
      progress.completedExercises.includes(ex.id)
    ).length;
    const avgScore =
      done > 0
        ? Math.round(
            level.exercises
              .filter((ex) => progress.completedExercises.includes(ex.id))
              .map((ex) => progress.scores[ex.id] ?? 0)
              .reduce((a, b) => a + b, 0) / done
          )
        : 0;
    return {
      id: level.id,
      name: level.name,
      tagline: level.tagline,
      color: level.color,
      done,
      total,
      pct: total > 0 ? Math.round((done / total) * 100) : 0,
      unlocked: done >= level.requiredExercises || level.id === "aprendiz",
      avgScore,
    };
  });
}
